import { useMemo } from 'react'
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'

import { getChatApiRoute } from '@renderer/chat/api/v1Client'
import { useAuth } from '@renderer/chat/providers'
import { useActiveStreamingChatIds } from '@renderer/chat/store'
import {
  applyOptimisticStreamingState,
  buildSidepanelChats,
  type SidepanelChat,
  type SidepanelChatApiItem,
} from './running-chat-selection'
import { FOR_YOU_CHATS_QUERY_KEY } from '@renderer/stores/conversation-query-keys'
import { forYouFeedKey, type ForYouChatsScope } from './running-chat-query-keys'
import { forYouChatsQueryOptions } from './for-you-chats-query'

export { forYouFeedKey, type ForYouChatsScope } from './running-chat-query-keys'

// Home and the full view share one 25-row page, matching Recents. Home renders
// five rows while keeping the rest ready for inline expansion.
const FOR_YOU_PAGE_LIMIT = 25

interface ConversationsResponse {
  data?: Array<SidepanelChatApiItem>
  nextCursor?: string
}

interface ConversationsPage {
  data: Array<SidepanelChatApiItem>
  nextCursor?: string
}

/** Generated API types do not yet include `surface=for-you` or `stagedActionIds`. */
async function fetchConversationsPage(token: string, query: string): Promise<ConversationsPage> {
  const response = await fetch(getChatApiRoute(`api/v1/conversations?${query}`), {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!response.ok) {
    throw new Error(`Failed to fetch conversations: ${response.status}`)
  }
  const json = (await response.json()) as ConversationsResponse
  const rows = json.data ?? []

  // Paired with `[conversations-gateway]`: that traces the event in, this
  // traces what the refetch it triggered actually came back with. Counting the
  // previews separately is the quickest way to tell a gateway that predates the
  // preview object (rows, no previews) from one that has it.
  console.log(`[sidepanel-chats] ?${query} → ${rows.length} row(s)`, {
    withPreview: rows.filter((row) => row.preview != null).length,
    streaming: rows.filter((row) => row.isStreaming).length,
    rows,
  })
  return { data: rows, nextCursor: json.nextCursor }
}

async function fetchConversations(token: string, query: string): Promise<Array<SidepanelChatApiItem>> {
  return (await fetchConversationsPage(token, query)).data
}

async function fetchConversationRow(token: string, conversationId: string): Promise<SidepanelChatApiItem | null> {
  const response = await fetch(getChatApiRoute(`api/v1/conversations/${conversationId}`), {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (response.status === 404) {
    return null
  }
  if (!response.ok) {
    throw new Error(`Failed to fetch conversation: ${response.status}`)
  }
  const json = (await response.json()) as { data?: SidepanelChatApiItem }
  return json.data ?? null
}

const SIDEPANEL_CHAT_ROW_QUERY_KEY = 'sidepanel-chat-row'

/** The row of a conversation the For You feed does not carry, such as a settled run with no open action. */
export function useSidepanelChatRow(conversationId: string | undefined, enabled: boolean): SidepanelChat | undefined {
  const { getAuthToken } = useAuth()
  const { data } = useQuery({
    queryKey: [SIDEPANEL_CHAT_ROW_QUERY_KEY, conversationId],
    queryFn: async () => fetchConversationRow(await getAuthToken(), conversationId!),
    enabled: enabled && !!conversationId,
    staleTime: 60 * 1000,
  })
  return useMemo(() => (data ? buildSidepanelChats([data])[0] : undefined), [data])
}

export interface ForYouChatsResult {
  data: Array<SidepanelChat>
  isLoading: boolean
  isError: boolean
  /** Identifies the feed these rows came from. Linger state is held per feed. */
  feedKey: string
}

export function useForYouChats(scope: ForYouChatsScope): ForYouChatsResult {
  const { getAuthToken } = useAuth()
  const queryClient = useQueryClient()
  const locallyStreamingChatIds = useActiveStreamingChatIds()
  const forYou = useQuery(
    forYouChatsQueryOptions({
      queryClient,
      fetchConversations: async (query) => fetchConversations(await getAuthToken(), query),
      scope,
      enabled: true,
    }),
  )

  const data = useMemo(
    () => applyOptimisticStreamingState(buildSidepanelChats(forYou.data), locallyStreamingChatIds),
    [forYou.data, locallyStreamingChatIds],
  )

  return {
    data,
    isLoading: forYou.isLoading,
    isError: forYou.isError,
    feedKey: forYouFeedKey(scope),
  }
}

export interface ForYouChatsFeedResult extends ForYouChatsResult {
  fetchNextPage: () => Promise<unknown>
  hasNextPage: boolean
  isFetchingNextPage: boolean
}

export function useForYouChatsFeed(archiveState: 'active' | 'archived' = 'active'): ForYouChatsFeedResult {
  const { getAuthToken } = useAuth()
  const locallyStreamingChatIds = useActiveStreamingChatIds()
  const feed = useInfiniteQuery({
    queryKey: archiveState === 'archived' ? [FOR_YOU_CHATS_QUERY_KEY, 'archived'] : [FOR_YOU_CHATS_QUERY_KEY],
    queryFn: async ({ pageParam }) => {
      const query = new URLSearchParams({
        surface: 'for-you',
        archiveState,
        includeChatActions: 'true',
        limit: String(FOR_YOU_PAGE_LIMIT),
      })
      if (pageParam) {
        query.set('nextCursor', pageParam)
      }
      return fetchConversationsPage(await getAuthToken(), query.toString())
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: 60 * 1000,
  })

  const data = useMemo(() => {
    const chats = buildSidepanelChats(feed.data?.pages.flatMap((page) => page.data))
    return archiveState === 'archived'
      ? chats.map((chat) => ({ ...chat, isStreaming: false, isUnread: false }))
      : applyOptimisticStreamingState(chats, locallyStreamingChatIds)
  }, [feed.data, locallyStreamingChatIds, archiveState])

  return {
    data,
    isLoading: feed.isLoading,
    isError: feed.isError,
    feedKey: archiveState === 'archived' ? `${forYouFeedKey()}:archived` : forYouFeedKey(),
    fetchNextPage: feed.fetchNextPage,
    hasNextPage: feed.hasNextPage === true,
    isFetchingNextPage: feed.isFetchingNextPage,
  }
}
